import { ReactNode } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface WritingSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  children: ReactNode;
  className?: string;
}

/**
 * WritingSheet — A calm, spacious overlay for long-form editorial writing.
 *
 * Replaces the cramped sidebar editor with a wide, page-like writing surface.
 * Follows Farmly writing-surface-v1: bg-card, generous padding, editorial feel.
 *
 * Usage:
 *   <WritingSheet open={open} onOpenChange={setOpen} title="Längre beskrivning">
 *     <RichTextEditor value={value} onChange={onChange} />
 *   </WritingSheet>
 */
export function WritingSheet({
  open,
  onOpenChange,
  title,
  description,
  children,
  className,
}: WritingSheetProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          'sm:max-w-3xl w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto p-0 gap-0',
          'bg-background border-border shadow-lg rounded-xl',
          '[&>button.absolute]:hidden', // hide default Dialog close button
          className,
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <DialogHeader className="space-y-0.5 p-0">
            <DialogTitle className="text-base font-medium text-foreground">
              {title}
            </DialogTitle>
            {description && (
              <DialogDescription className="text-sm text-muted-foreground">
                {description}
              </DialogDescription>
            )}
          </DialogHeader>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onOpenChange(false)}
            className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Stäng</span>
          </Button>
        </div>

        {/* Writing surface */}
        <div className="px-6 py-5">
          {children}
        </div>

        {/* Footer */}
        <div className="flex justify-end px-6 py-3 border-t border-border bg-muted/30">
          <Button
            size="sm"
            onClick={() => onOpenChange(false)}
          >
            Klar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
